import { useCallback } from 'react';
import { callBackend } from '../../../utils/api';
import {
    POS_SIZING_RISK_SYNC_REQUIRED_CODE,
    isPosSizingRiskSyncRequired,
} from '../../../utils/posSizingRiskReadiness';
import { formatUserFactorModel } from '../../../utils/factorDataOptions';
import { normalizeBetaSource } from '../../../utils/betaSources';
import { buildRiskRequestKey } from '../orchestration/requestRegistry';
import {
    RISK_OVERVIEW_RESOURCE_STATES,
    withRiskOverviewResource,
    buildUnavailableResources,
} from '../riskOverviewResource';
import useSingleFlightRequests from './useSingleFlightRequests';

const IDENTITY_PARTIAL_STATUS = 'IDENTITY_PARTIAL';
const IDENTITY_UNRESOLVED_ALL_STATUS = 'IDENTITY_UNRESOLVED_ALL';

const normalizeText = (value) => {
    if (value == null) {
        return '';
    }
    return String(value).trim();
};

const resolveIdioVolSourceFromContext = ({
    matricesOverride,
    riskContextOverride,
    currentIdioVolSource,
}) => (
    matricesOverride?.idioVolSource
    ?? matricesOverride?.idio_vol_source
    ?? matricesOverride?.idio_source
    ?? riskContextOverride?.idioVolSource
    ?? currentIdioVolSource
    ?? null
);

const normalizeRiskStatus = (status) => normalizeText(status).toUpperCase();

const buildNonFatalIdentityWarning = ({
    status = '',
    message = '',
    partialMessage,
    unresolvedMessage,
} = {}) => {
    if (status === IDENTITY_UNRESOLVED_ALL_STATUS) {
        return message || unresolvedMessage;
    }
    return message || partialMessage;
};

const isNonFatalIdentityStatus = (status) => (
    status === IDENTITY_PARTIAL_STATUS || status === IDENTITY_UNRESOLVED_ALL_STATUS
);

const getNonFatalIdentityResourceState = (status) => {
    if (status === IDENTITY_PARTIAL_STATUS) {
        return RISK_OVERVIEW_RESOURCE_STATES.PARTIAL;
    }
    if (status === IDENTITY_UNRESOLVED_ALL_STATUS) {
        return RISK_OVERVIEW_RESOURCE_STATES.EMPTY;
    }
    return undefined;
};

const requireRiskPublicationContract = ({
    operation,
    generation,
    resourceScopeKey,
    stagePublicationState,
}) => {
    if (
        !Number.isInteger(generation)
        || generation < 0
        || typeof resourceScopeKey !== 'string'
        || resourceScopeKey.trim() === ''
        || typeof stagePublicationState !== 'function'
    ) {
        throw new Error(
            `${operation} requires an explicit publication transaction, generation, and scope.`,
        );
    }
};

const isExplicitFailedRiskResult = (payload = {}) => {
    const status = normalizeRiskStatus(payload?.status);
    return payload?.success === false && !isNonFatalIdentityStatus(status);
};

export default function useRiskDataFetchers({
    enableHoldingsCalculation,
    userUid,
    selectedFactorModel,
    selectedBetaSource,
    factorSnapshotDate,
    factorDataModel,
    currentIdioVolSource = null,
    actualPortfolioId,
    holdingsRequestIdRef,
    groupRiskRequestIdRef,
    optimizedRiskRequestIdRef,
    setCalculations,
    setHoldings,
    setHoldingsLoading,
    setCalculationsLoading,
    setGmv,
    setError,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
    setOptimizedGroupRiskMetricsLoading,
    setGroupRisksLoading,
    setOptimizedGroupRiskMetrics,
    setGroupRiskMetrics,
    setOptimizedRiskMetrics,
}) {
    const runSingleFlight = useSingleFlightRequests();
    const groupedRiskEndpoint = 'fetch_attribute_risk_metrics';
    const groupedRiskLabel = 'Attribute';

    const fetchRiskMetrics = useCallback(
        async (
            portfolioId,
            matricesOverride = null,
            riskContextOverride = null,
            targetKey = null,
            generation,
            resourceScopeKey,
        ) => {
            if (!enableHoldingsCalculation || !userUid) return null;
            if (portfolioId == null) return null;

            const snapshotDate = (
                matricesOverride?.factorSnapshotDate
                ?? matricesOverride?.factor_snapshot_date
                ?? riskContextOverride?.factorSnapshotDate
                ?? factorSnapshotDate
            );
            const resolvedFactorModel = formatUserFactorModel(
                riskContextOverride?.factorModel ?? selectedFactorModel,
            );
            const resolvedBetaSource = normalizeBetaSource(
                riskContextOverride?.betaSource ?? selectedBetaSource,
            );
            const resolvedFactorDataModel =
                matricesOverride?.factorDataModel
                ?? matricesOverride?.factor_data_model
                ?? riskContextOverride?.factorDataModel
                ?? factorDataModel;
            const resolvedIdioVolSource = resolveIdioVolSourceFromContext({
                matricesOverride,
                riskContextOverride,
                currentIdioVolSource,
            });

            const singleFlightKey = buildRiskRequestKey([
                'calculate_holdings',
                userUid,
                portfolioId,
                targetKey ?? '',
                resolvedFactorModel ?? '',
                resolvedBetaSource ?? '',
                snapshotDate ?? '',
                resolvedFactorDataModel ?? '',
                resolvedIdioVolSource ?? '',
                resourceScopeKey,
            ]);
            const scopeKey = resourceScopeKey;
            try {
                const payload = await runSingleFlight(singleFlightKey, () => callBackend(
                    'calculate_holdings',
                    'POST',
                    {
                        factorModel: resolvedFactorModel,
                        portfolioId,
                        beta_source: resolvedBetaSource,
                        factor_snapshot_date: snapshotDate,
                        factor_data_model: resolvedFactorDataModel,
                        idio_vol_source: resolvedIdioVolSource,
                    },
                ));
                const explicitFailure = isExplicitFailedRiskResult(payload);
                const isSelected = targetKey === 'opt' || targetKey === 'pos';
                const safeMessage = isSelected
                    ? 'Selected portfolio risk is temporarily unavailable.'
                    : 'Risk metrics are temporarily unavailable.';
                const resourcePayload = explicitFailure
                    ? {
                        success: false,
                        status: 'RISK_OVERVIEW_UNAVAILABLE',
                        message: safeMessage,
                        holdingsCalculations: [],
                        factorExposures: [],
                    }
                    : payload;
                const resourceStatus = normalizeRiskStatus(resourcePayload?.status);
                return withRiskOverviewResource(resourcePayload, {
                    scopeKey,
                    generation,
                    state: getNonFatalIdentityResourceState(resourceStatus),
                    errorCode: explicitFailure ? 'risk_overview_unavailable' : null,
                    safeMessage: explicitFailure ? safeMessage : null,
                });
            } catch (error) {
                return buildUnavailableResources({
                    scopeKey,
                    generation,
                    isSelected: targetKey === 'opt' || targetKey === 'pos',
                    posSizingSyncRequired: isPosSizingRiskSyncRequired(error),
                }).primary;
            }
        },
        [
            enableHoldingsCalculation,
            userUid,
            currentIdioVolSource,
            factorDataModel,
            factorSnapshotDate,
            runSingleFlight,
            selectedBetaSource,
            selectedFactorModel,
        ],
    );

    const createUnavailablePublication = useCallback(({
        scopeKey, generation, targetKey, posSizingSyncRequired = false,
    }) => {
        const isSelected = targetKey === 'opt' || targetKey === 'pos';
        const resources = buildUnavailableResources({
            scopeKey, generation, isSelected, posSizingSyncRequired,
        });
        return {
            ...resources,
            publish: () => {
                if (isSelected) {
                    setOptimizedRiskMetrics(resources.primary);
                    setOptimizedGroupRiskMetrics(resources.group);
                } else {
                    setCalculations(resources.primary);
                    setHoldings([]);
                    setGmv(null);
                    setGroupRiskMetrics(resources.group);
                    setError(null);
                }
            },
        };
    }, [
        setCalculations,
        setError,
        setGmv,
        setHoldings,
        setOptimizedRiskMetrics,
        setOptimizedGroupRiskMetrics,
        setGroupRiskMetrics,
    ]);

    const fetchHoldings = useCallback(async (
        portfolioIdOverride = null,
        matricesOverride = null,
        targetKey = null,
        {
            isCurrentRequest = null,
            riskContextOverride = null,
            stagePublicationState,
            stagePresentationState = null,
            generation,
            resourceScopeKey,
        } = {},
    ) => {
        const portfolioId = portfolioIdOverride ?? actualPortfolioId;
        if (portfolioId == null) return null;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) return null;
        requireRiskPublicationContract({
            operation: 'fetchHoldings',
            generation,
            resourceScopeKey,
            stagePublicationState,
        });
        const requestId = ++holdingsRequestIdRef.current;
        const present = apply => { if (!stagePresentationState) apply(); };
        let startedLoading = false;

        present(() => setHoldingsLoading(true));
        present(() => setCalculationsLoading(true));
        startedLoading = true;
        try {
            const result = await fetchRiskMetrics(
                portfolioId,
                matricesOverride,
                riskContextOverride,
                targetKey ?? 'actual',
                generation,
                resourceScopeKey,
            );
            if (holdingsRequestIdRef.current !== requestId || !isCurrent()) {
                return null;
            }
            if (!result) {
                throw new Error('No holdings result was returned.');
            }
            if (isExplicitFailedRiskResult(result)) {
                const resourceMetadata = result.riskOverviewResource;
                const unavailable = createUnavailablePublication({
                    scopeKey: resourceMetadata.scopeKey,
                    generation: resourceMetadata.generation,
                    targetKey: 'actual',
                    posSizingSyncRequired: resourceMetadata.errorCode
                        === POS_SIZING_RISK_SYNC_REQUIRED_CODE,
                });
                stagePublicationState(
                    () => {
                        unavailable.publish();
                        if (stagePresentationState) {
                            setHoldingsLoading(false);
                            setCalculationsLoading(false);
                        }
                    },
                    () => holdingsRequestIdRef.current === requestId && isCurrent(),
                    {
                        panel: 'primary',
                        resource: unavailable.primary,
                        scopeKey: resourceScopeKey,
                        generation,
                    },
                );
                return { ...unavailable.primary, loaded: true };
            }

            const normalizedResult = { ...result };
            const holdingsRows = Array.isArray(normalizedResult?.holdingsCalculations)
                ? normalizedResult.holdingsCalculations
                : [];
            const normalizedStatus = normalizeRiskStatus(normalizedResult?.status);
            const isIdentityNonFatal = isNonFatalIdentityStatus(normalizedStatus);

            if (Array.isArray(normalizedResult?.holdingsCalculations) || isIdentityNonFatal || normalizedResult?.success === true) {
                const holdingsData = holdingsRows.map((h) => ({
                    id: h.cognive_id ?? h.id ?? h.ticker ?? null,
                    sedol: h.display_sedol ?? h.sedol ?? null,
                    display_sedol: h.display_sedol ?? h.sedol ?? null,
                    ...h,
                }));
                const publishHoldings = () => {
                    if (stagePresentationState) {
                        setHoldingsLoading(false);
                        setCalculationsLoading(false);
                    }
                    setCalculations(normalizedResult);
                    setHoldings(holdingsData);
                    setGmv(normalizedResult.gmv);
                    setError(null);
                    if (isIdentityNonFatal) {
                        setSnackbarSeverity('warning');
                        setSnackbarMessage(
                            buildNonFatalIdentityWarning({
                                status: normalizedStatus,
                                message: normalizedResult.message,
                                partialMessage:
                                    'Some holdings could not be identity-resolved; showing resolved rows only.',
                                unresolvedMessage:
                                    'No holdings resolved into startup identity scope. Review MSI drift choices and retry.',
                            }),
                        );
                        setSnackbarOpen(true);
                    }
                };
                stagePublicationState(
                    publishHoldings,
                    () => holdingsRequestIdRef.current === requestId && isCurrent(),
                    {
                        panel: 'primary',
                        resource: normalizedResult,
                        scopeKey: resourceScopeKey,
                        generation,
                    },
                );
            } else {
                throw new Error('Invalid data structure returned from calculate_holdings function.');
            }
            return { ...normalizedResult, loaded: true };
        } catch (fetchError) {
            if (holdingsRequestIdRef.current !== requestId || !isCurrent()) {
                return null;
            }
            console.error('Error fetching or calculating holdings.');
            present(() => setError('Failed to load holdings and calculations. Please try again.'));
            present(() => setSnackbarMessage('Failed to load holdings and calculations.'));
            present(() => setSnackbarSeverity('error'));
            present(() => setSnackbarOpen(true));
            throw fetchError;
        } finally {
            if (startedLoading && holdingsRequestIdRef.current === requestId) {
                present(() => setHoldingsLoading(false));
                present(() => setCalculationsLoading(false));
            }
        }
    }, [
        actualPortfolioId,
        createUnavailablePublication,
        fetchRiskMetrics,
        holdingsRequestIdRef,
        setCalculations,
        setCalculationsLoading,
        setError,
        setGmv,
        setHoldings,
        setHoldingsLoading,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
    ]);

    const fetchFactorPositions = useCallback(async ({
        factorName,
        snapshotToken,
        portfolioId = actualPortfolioId,
        riskContextOverride = null,
        signal = undefined,
    } = {}) => {
        const normalizedFactor = normalizeText(factorName);
        const normalizedSnapshotToken = normalizeText(snapshotToken);
        if (
            !enableHoldingsCalculation
            || !userUid
            || portfolioId == null
            || !normalizedFactor
            || !normalizedSnapshotToken
        ) {
            throw new Error('Factor position detail requires a current risk snapshot.');
        }
        const resolvedFactorModel = formatUserFactorModel(
            riskContextOverride?.factorModel ?? selectedFactorModel,
        );
        const resolvedBetaSource = normalizeBetaSource(
            riskContextOverride?.betaSource ?? selectedBetaSource,
        );
        const resolvedFactorDataModel = (
            riskContextOverride?.factorDataModel ?? factorDataModel
        );
        const resolvedSnapshotDate = (
            riskContextOverride?.factorSnapshotDate ?? factorSnapshotDate
        );
        const resolvedIdioVolSource = (
            riskContextOverride?.idioVolSource ?? currentIdioVolSource
        );
        return callBackend(
            'fetch_factor_positions',
            'POST',
            {
                factorModel: resolvedFactorModel,
                portfolioId,
                beta_source: resolvedBetaSource,
                factor_snapshot_date: resolvedSnapshotDate,
                factor_data_model: resolvedFactorDataModel,
                idio_vol_source: resolvedIdioVolSource,
                factor: normalizedFactor,
                snapshot_token: normalizedSnapshotToken,
            },
            { signal },
        );
    }, [
        actualPortfolioId,
        currentIdioVolSource,
        enableHoldingsCalculation,
        factorDataModel,
        factorSnapshotDate,
        selectedBetaSource,
        selectedFactorModel,
        userUid,
    ]);

    const fetchGroupRiskMetrics = useCallback(async ({
        portfolioId,
        targetKey,
        matricesOverride = null,
        riskContextOverride = null,
        isCurrentRequest = null,
        stagePublicationState,
        stagePresentationState = null,
        generation,
        resourceScopeKey,
    }) => {
        if (!userUid) return;
        if (portfolioId == null) return;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) return;
        requireRiskPublicationContract({
            operation: 'fetchGroupRiskMetrics',
            generation,
            resourceScopeKey,
            stagePublicationState,
        });

        const snapshotDate = (
            matricesOverride?.factorSnapshotDate
            ?? matricesOverride?.factor_snapshot_date
            ?? riskContextOverride?.factorSnapshotDate
            ?? factorSnapshotDate
        );
        const resolvedFactorModel = formatUserFactorModel(
            riskContextOverride?.factorModel ?? selectedFactorModel,
        );
        const resolvedBetaSource = normalizeBetaSource(
            riskContextOverride?.betaSource ?? selectedBetaSource,
        );
        const resolvedFactorDataModel =
            matricesOverride?.factorDataModel
            ?? matricesOverride?.factor_data_model
            ?? riskContextOverride?.factorDataModel
            ?? factorDataModel;
        const resolvedIdioVolSource = resolveIdioVolSourceFromContext({
            matricesOverride,
            riskContextOverride,
            currentIdioVolSource,
        });

        const resolvedTargetKey = targetKey ?? 'actual';
        const isOptimized = resolvedTargetKey === 'opt' || resolvedTargetKey === 'pos';

        const groupReq = groupRiskRequestIdRef.current;
        groupReq[resolvedTargetKey] = (groupReq[resolvedTargetKey] || 0) + 1;
        const requestId = groupReq[resolvedTargetKey];
        const present = apply => { if (!stagePresentationState) apply(); };
        let startedLoading = false;
        if (isOptimized) {
            present(() => setOptimizedGroupRiskMetricsLoading(true));
        } else {
            present(() => setGroupRisksLoading(true));
        }
        startedLoading = true;

        const singleFlightKey = buildRiskRequestKey([
            groupedRiskEndpoint,
            userUid,
            portfolioId,
            resolvedTargetKey,
            resolvedFactorModel ?? '',
            resolvedBetaSource ?? '',
            snapshotDate ?? '',
            resolvedFactorDataModel ?? '',
            resolvedIdioVolSource ?? '',
            resourceScopeKey,
        ]);
        const scopeKey = resourceScopeKey;

        try {
            const result = await runSingleFlight(singleFlightKey, () => callBackend(
                groupedRiskEndpoint,
                'POST',
                {
                    portfolio_id: portfolioId,
                    factorModel: resolvedFactorModel,
                    beta_source: resolvedBetaSource,
                    factor_snapshot_date: snapshotDate,
                    factor_data_model: resolvedFactorDataModel,
                    idio_vol_source: resolvedIdioVolSource,
                },
            ));

            if (groupRiskRequestIdRef.current[resolvedTargetKey] !== requestId || !isCurrent()) {
                return null;
            }

            const explicitFailure = isExplicitFailedRiskResult(result);
            const publishableResult = explicitFailure
                ? {
                    success: false,
                    status: 'RISK_OVERVIEW_UNAVAILABLE',
                    message: 'Attribute risk metrics are temporarily unavailable.',
                    portfolio_risks: {},
                    attribute_definitions: [],
                    attribute_results: [],
                }
                : result;
            const normalizedStatus = normalizeRiskStatus(publishableResult?.status);
            const normalizedResult = withRiskOverviewResource({
                ...publishableResult,
                status: normalizedStatus,
            }, {
                scopeKey,
                generation,
                state: getNonFatalIdentityResourceState(normalizedStatus),
                errorCode: explicitFailure ? 'risk_overview_unavailable' : null,
                safeMessage: explicitFailure
                    ? 'Attribute risk metrics are temporarily unavailable.'
                    : null,
            });
            if (explicitFailure) {
                const unavailable = createUnavailablePublication({
                    scopeKey,
                    generation,
                    targetKey: resolvedTargetKey,
                });
                stagePublicationState(
                    () => {
                        unavailable.publish();
                        if (stagePresentationState) {
                            if (isOptimized) setOptimizedGroupRiskMetricsLoading(false);
                            else setGroupRisksLoading(false);
                        }
                    },
                    () => (
                        groupRiskRequestIdRef.current[resolvedTargetKey] === requestId
                        && isCurrent()
                    ),
                    {
                        panel: 'group',
                        resource: unavailable.group,
                        scopeKey,
                        generation,
                    },
                );
                return unavailable.group;
            }
            const publishGroupRisk = () => {
                if (stagePresentationState) {
                    if (isOptimized) setOptimizedGroupRiskMetricsLoading(false);
                    else setGroupRisksLoading(false);
                }
                if (isOptimized) {
                    setOptimizedGroupRiskMetrics(normalizedResult);
                } else {
                    setGroupRiskMetrics(normalizedResult);
                }

                if (
                    normalizedStatus === IDENTITY_PARTIAL_STATUS
                    || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS
                ) {
                    setError(null);
                    setSnackbarSeverity('warning');
                    setSnackbarMessage(
                        buildNonFatalIdentityWarning({
                            status: normalizedStatus,
                            message: normalizedResult.message,
                            partialMessage:
                                `Some holdings could not be identity-resolved for ${groupedRiskLabel.toLowerCase()} risk metrics.`,
                            unresolvedMessage:
                                `No securities resolved into ${groupedRiskLabel.toLowerCase()}-risk identity scope.`,
                        }),
                    );
                    setSnackbarOpen(true);
                } else if (normalizedResult.success === true) {
                    setError(null);
                } else if (normalizedResult.status === 'NO_COMMON_SECURITIES') {
                    setError(null);
                    setSnackbarSeverity('warning');
                    setSnackbarMessage(normalizedResult.message || 'No common securities found.');
                    setSnackbarOpen(true);
                } else if (normalizedResult.status === 'NO_PORTFOLIO_FOUND') {
                    setSnackbarMessage(normalizedResult.message || 'No portfolio found.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                } else {
                    setError(`Failed to load ${groupedRiskLabel.toLowerCase()} risk metrics.`);
                    setSnackbarMessage(`Failed to load ${groupedRiskLabel.toLowerCase()} risk metrics.`);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            };
            stagePublicationState(
                publishGroupRisk,
                () => (
                    groupRiskRequestIdRef.current[resolvedTargetKey] === requestId
                    && isCurrent()
                ),
                {
                    panel: 'group',
                    resource: normalizedResult,
                    scopeKey,
                    generation,
                },
            );
            return normalizedResult;
        } catch (error) {
            if (groupRiskRequestIdRef.current[resolvedTargetKey] !== requestId || !isCurrent()) {
                return null;
            }
            const posSizingSyncRequired = isPosSizingRiskSyncRequired(error);
            if (!posSizingSyncRequired) {
                console.error('Error fetching attribute risk metrics.');
            }
            const unavailable = createUnavailablePublication({
                scopeKey,
                generation,
                targetKey: resolvedTargetKey,
                posSizingSyncRequired,
            });
            stagePublicationState(
                () => {
                    unavailable.publish();
                    if (stagePresentationState) {
                        if (isOptimized) setOptimizedGroupRiskMetricsLoading(false);
                        else setGroupRisksLoading(false);
                    }
                },
                () => (
                    groupRiskRequestIdRef.current[resolvedTargetKey] === requestId
                    && isCurrent()
                ),
                {
                    panel: 'group',
                    resource: unavailable.group,
                    scopeKey,
                    generation,
                },
            );
            return unavailable.group;
        } finally {
            if (
                startedLoading
                && groupRiskRequestIdRef.current[resolvedTargetKey] === requestId
            ) {
                if (isOptimized) {
                    present(() => setOptimizedGroupRiskMetricsLoading(false));
                } else {
                    present(() => setGroupRisksLoading(false));
                }
            }
        }
    }, [
        createUnavailablePublication,
        currentIdioVolSource,
        factorDataModel,
        factorSnapshotDate,
        groupedRiskEndpoint,
        groupedRiskLabel,
        runSingleFlight,
        groupRiskRequestIdRef,
        selectedBetaSource,
        selectedFactorModel,
        setError,
        setOptimizedGroupRiskMetrics,
        setOptimizedGroupRiskMetricsLoading,
        setGroupRiskMetrics,
        setGroupRisksLoading,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    const refreshActualRiskLensForIdioSource = useCallback(async ({
        idioVolSource,
        riskContextOverride: riskContextOverrideParam = null,
        isCurrentRequest = null,
        stagePublicationState,
        generation,
        resourceScopeKey,
    } = {}) => {
        if (actualPortfolioId == null) {
            return null;
        }
        requireRiskPublicationContract({
            operation: 'refreshActualRiskLensForIdioSource',
            generation,
            resourceScopeKey,
            stagePublicationState,
        });
        // Converted risk endpoints load matrices server-side, so this
        // refresh no longer fetches or hydrates a matricesOverride. It
        // only carries the factor risk context that the backend needs
        // to select the right cached matrix payload.
        const factorRiskContext = {
            factorModel: riskContextOverrideParam?.factorModel ?? selectedFactorModel,
            factorDataModel:
                riskContextOverrideParam?.factorDataModel ?? factorDataModel,
            factorSnapshotDate:
                riskContextOverrideParam?.factorSnapshotDate ?? factorSnapshotDate,
            betaSource:
                riskContextOverrideParam?.betaSource ?? selectedBetaSource,
            idioVolSource: idioVolSource
                ?? riskContextOverrideParam?.idioVolSource
                ?? currentIdioVolSource,
        };

        await fetchHoldings(
            actualPortfolioId,
            null,
            'actual',
            {
                riskContextOverride: factorRiskContext,
                isCurrentRequest,
                stagePublicationState,
                generation,
                resourceScopeKey,
            },
        );

        await fetchGroupRiskMetrics({
            portfolioId: actualPortfolioId,
            targetKey: 'actual',
            riskContextOverride: factorRiskContext,
            isCurrentRequest,
            stagePublicationState,
            generation,
            resourceScopeKey,
        });
    }, [
        actualPortfolioId,
        currentIdioVolSource,
        factorDataModel,
        factorSnapshotDate,
        fetchHoldings,
        fetchGroupRiskMetrics,
        selectedBetaSource,
        selectedFactorModel,
    ]);

    const fetchOptimizedRiskMetrics = useCallback(async (
        portfolioId,
        matricesOverride = null,
        riskContextOverride = null,
        {
            isCurrentRequest = null,
            stagePublicationState,
            generation,
            targetKey = 'opt',
            resourceScopeKey,
        } = {},
    ) => {
        if (portfolioId == null) return;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;
        if (!isCurrent()) return;
        requireRiskPublicationContract({
            operation: 'fetchOptimizedRiskMetrics',
            generation,
            resourceScopeKey,
            stagePublicationState,
        });
        const requestId = ++optimizedRiskRequestIdRef.current;
        const result = await fetchRiskMetrics(
            portfolioId,
            matricesOverride,
            riskContextOverride,
            targetKey,
            generation,
            resourceScopeKey,
        );
        if (optimizedRiskRequestIdRef.current !== requestId || !isCurrent()) {
            return { loaded: false, stale: true };
        }
        if (!result || typeof result !== 'object') {
            throw new Error('No optimized risk result was returned.');
        }
        const isUnavailable = (
            result?.riskOverviewResource?.state === RISK_OVERVIEW_RESOURCE_STATES.ERROR
        );
        const unavailable = isUnavailable
            ? createUnavailablePublication({
                scopeKey: resourceScopeKey,
                generation,
                targetKey,
                posSizingSyncRequired: result.riskOverviewResource.errorCode
                    === POS_SIZING_RISK_SYNC_REQUIRED_CODE,
            })
            : null;
        const publishOptimizedRisk = unavailable?.publish
            ?? (() => setOptimizedRiskMetrics(result));
        const publishedResource = unavailable?.primary ?? result;
        stagePublicationState(
            publishOptimizedRisk,
            () => optimizedRiskRequestIdRef.current === requestId && isCurrent(),
            {
                panel: 'primary',
                resource: publishedResource,
                scopeKey: resourceScopeKey,
                generation,
            },
        );
        return {
            ...publishedResource,
            loaded: !isUnavailable,
        };
    }, [
        createUnavailablePublication,
        fetchRiskMetrics,
        optimizedRiskRequestIdRef,
        setOptimizedRiskMetrics,
    ]);

    return {
        fetchFactorPositions,
        fetchHoldings,
        fetchGroupRiskMetrics,
        refreshActualRiskLensForIdioSource,
        fetchOptimizedRiskMetrics,
    };
}
